import { useState, useEffect, useRef, useCallback } from "react";
import Peer from "simple-peer";
import { getSocket } from "../../../socket";
import {
  PhoneIcon,
  VideoIcon,
  MuteMicIcon,
  MicrophoneIcon,
  CameraOffIcon,
  EndCallIcon,
} from "../../../svg/chat";
import "./style.css";

// Ícone de gravação
const RecordIcon = ({ color = "#fff" }) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill={color}>
    <circle cx="12" cy="12" r="8" />
  </svg>
);

// Ícone de parar gravação
const StopRecordIcon = ({ color = "#fff" }) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill={color}>
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

// Ícone de download
const DownloadIcon = ({ color = "#fff" }) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill={color}>
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
  </svg>
);

export default function CallInterface({
  callData,
  user,
  onClose,
  isIncoming = false,
}) {
  const [callStatus, setCallStatus] = useState(
    isIncoming ? "incoming" : "calling"
  );
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  // Estados de gravação
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [showRecordingModal, setShowRecordingModal] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const timerRef = useRef(null);

  // Refs de gravação
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const combinedStreamRef = useRef(null);

  const { callId, type, caller, targetUserId } = callData;

  // ICE servers (using Google's free STUN servers)
  const iceServers = {
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
      { urls: "stun:stun2.l.google.com:19302" },
    ],
  };

  // Combinar streams local e remoto para gravação
  const createCombinedStream = useCallback(() => {
    const audioContext = new AudioContext();
    const destination = audioContext.createMediaStreamDestination();

    // Adicionar áudio local
    if (localStreamRef.current) {
      const localAudioTracks = localStreamRef.current.getAudioTracks();
      if (localAudioTracks.length > 0) {
        const localSource = audioContext.createMediaStreamSource(
          new MediaStream(localAudioTracks)
        );
        localSource.connect(destination);
      }
    }

    // Adicionar áudio remoto
    if (remoteStreamRef.current) {
      const remoteAudioTracks = remoteStreamRef.current.getAudioTracks();
      if (remoteAudioTracks.length > 0) {
        const remoteSource = audioContext.createMediaStreamSource(
          new MediaStream(remoteAudioTracks)
        );
        remoteSource.connect(destination);
      }
    }

    // Para videochamadas, criar um canvas para combinar os vídeos
    if (type === "video") {
      const canvas = document.createElement("canvas");
      canvas.width = 1280;
      canvas.height = 720;
      const ctx = canvas.getContext("2d");

      const drawFrame = () => {
        // Desenhar vídeo remoto (tela cheia)
        if (remoteVideoRef.current && remoteVideoRef.current.srcObject) {
          ctx.drawImage(remoteVideoRef.current, 0, 0, canvas.width, canvas.height);
        }

        // Desenhar vídeo local (picture-in-picture)
        if (localVideoRef.current && localVideoRef.current.srcObject) {
          const pipWidth = 200;
          const pipHeight = 150;
          ctx.drawImage(
            localVideoRef.current,
            canvas.width - pipWidth - 20,
            20,
            pipWidth,
            pipHeight
          );
        }

        if (isRecording) {
          requestAnimationFrame(drawFrame);
        }
      };

      drawFrame();

      const videoStream = canvas.captureStream(30);
      const combinedStream = new MediaStream([
        ...videoStream.getVideoTracks(),
        ...destination.stream.getAudioTracks(),
      ]);

      return combinedStream;
    }

    // Para chamadas de voz, apenas áudio
    return destination.stream;
  }, [type, isRecording]);

  // Iniciar gravação
  const startRecording = useCallback(() => {
    try {
      recordedChunksRef.current = [];
      setRecordingDuration(0);

      // Criar stream combinado
      const streamToRecord = createCombinedStream();
      combinedStreamRef.current = streamToRecord;

      // Definir mimeType baseado no tipo de chamada
      const mimeType = type === "video"
        ? "video/webm;codecs=vp9,opus"
        : "audio/webm;codecs=opus";

      const options = { mimeType };

      // Verificar suporte
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        // Fallback
        options.mimeType = type === "video" ? "video/webm" : "audio/webm";
      }

      const mediaRecorder = new MediaRecorder(streamToRecord, options);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: type === "video" ? "video/webm" : "audio/webm",
        });
        setRecordedBlob(blob);
        setShowRecordingModal(true);
      };

      mediaRecorder.start(1000); // Coletar dados a cada segundo
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);

      // Timer de duração da gravação
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);

      console.log("Recording started");
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Não foi possível iniciar a gravação");
    }
  }, [type, createCombinedStream]);

  // Parar gravação
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }

      console.log("Recording stopped");
    }
  }, []);

  // Download da gravação
  const downloadRecording = useCallback(() => {
    if (!recordedBlob) return;

    const url = URL.createObjectURL(recordedBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chamada_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.${type === "video" ? "webm" : "webm"}`;
    document.body.appendChild(a);
    a.click();
    if (a.parentNode) {
      document.body.removeChild(a);
    }
    URL.revokeObjectURL(url);

    setShowRecordingModal(false);
    setRecordedBlob(null);
  }, [recordedBlob, type]);

  // Descartar gravação
  const discardRecording = useCallback(() => {
    setRecordedBlob(null);
    setShowRecordingModal(false);
  }, []);

  const initializeMedia = useCallback(async () => {
    try {
      const constraints = {
        audio: true,
        video: type === "video",
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      return stream;
    } catch (error) {
      console.error("Error accessing media devices:", error);
      alert("Não foi possível acessar câmera/microfone. Verifique as permissões.");
      onClose();
    }
  }, [type, onClose]);

  const startCall = useCallback(async () => {
    const stream = await initializeMedia();
    if (!stream) return;

    const socket = getSocket();
    if (!socket) return;

    // Create peer as initiator
    const peer = new Peer({
      initiator: true,
      trickle: false,
      stream,
      config: iceServers,
    });

    peer.on("signal", (signal) => {
      socket.emit("call:signal", {
        callId,
        targetUserId,
        signal,
      });
    });

    peer.on("stream", (remoteStream) => {
      remoteStreamRef.current = remoteStream;
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
      }
    });

    peer.on("connect", () => {
      setCallStatus("connected");
      startTimer();
    });

    peer.on("error", (err) => {
      console.error("Peer error:", err);
      endCall();
    });

    peerRef.current = peer;

    // Listen for answer
    socket.on("call:answered", ({ signal }) => {
      if (peerRef.current) {
        peerRef.current.signal(signal);
      }
    });

    socket.on("call:rejected", () => {
      setCallStatus("rejected");
      setTimeout(() => onClose(), 2000);
    });

    socket.on("call:ended", () => {
      endCall();
    });
  }, [callId, targetUserId, initializeMedia, iceServers]);

  const answerCall = useCallback(async () => {
    const stream = await initializeMedia();
    if (!stream) return;

    const socket = getSocket();
    if (!socket) return;

    setCallStatus("connecting");

    // Listen for signal from caller
    socket.on("call:signal", ({ signal }) => {
      if (!peerRef.current) {
        // Create peer as non-initiator
        const peer = new Peer({
          initiator: false,
          trickle: false,
          stream,
          config: iceServers,
        });

        peer.on("signal", (answerSignal) => {
          socket.emit("call:answer", {
            callId,
            signal: answerSignal,
          });
        });

        peer.on("stream", (remoteStream) => {
          remoteStreamRef.current = remoteStream;
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
          }
        });

        peer.on("connect", () => {
          setCallStatus("connected");
          startTimer();
        });

        peer.on("error", (err) => {
          console.error("Peer error:", err);
          endCall();
        });

        peerRef.current = peer;
        peer.signal(signal);
      }
    });

    socket.on("call:ended", () => {
      endCall();
    });
  }, [callId, initializeMedia, iceServers]);

  const rejectCall = () => {
    const socket = getSocket();
    if (socket) {
      socket.emit("call:reject", { callId });
    }
    cleanup();
    onClose();
  };

  const endCall = () => {
    // Parar gravação se estiver ativa
    if (isRecording) {
      stopRecording();
    }

    const socket = getSocket();
    if (socket) {
      socket.emit("call:end", { callId });
    }
    cleanup();

    // Se não há gravação para mostrar, fechar imediatamente
    if (!recordedBlob && !isRecording) {
      onClose();
    }
  };

  const cleanup = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (combinedStreamRef.current) {
      combinedStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (peerRef.current) {
      peerRef.current.destroy();
    }
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      videoTracks.forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs.toString().padStart(2, "0")}:${mins
        .toString()
        .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!isIncoming) {
      startCall();
    }

    return () => {
      cleanup();
    };
  }, [isIncoming, startCall]);

  // Fechar modal e interface quando descartar
  const handleCloseRecordingModal = () => {
    discardRecording();
    onClose();
  };

  return (
    <div className={`call_interface ${type}`}>
      {/* Background / Remote video */}
      {type === "video" && callStatus === "connected" && (
        <video
          ref={remoteVideoRef}
          className="remote_video"
          autoPlay
          playsInline
        />
      )}

      {/* Indicador de gravação */}
      {isRecording && (
        <div className="recording_badge">
          <span className="recording_dot"></span>
          <span className="recording_text">REC</span>
          <span className="recording_time">{formatDuration(recordingDuration)}</span>
        </div>
      )}

      {/* Call info overlay */}
      <div className="call_overlay">
        {/* User info */}
        <div className="call_user_info">
          {(callStatus !== "connected" || type === "voice") && (
            <img
              src={
                caller?.picture ||
                "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png"
              }
              alt={caller?.name || "User"}
              className="call_avatar"
            />
          )}
          <h2 className="call_user_name">{caller?.name || "Desconhecido"}</h2>
          <p className="call_status">
            {callStatus === "incoming" && "Chamada recebida..."}
            {callStatus === "calling" && "Chamando..."}
            {callStatus === "connecting" && "Conectando..."}
            {callStatus === "connected" && formatDuration(callDuration)}
            {callStatus === "rejected" && "Chamada rejeitada"}
          </p>
        </div>

        {/* Local video (picture-in-picture) */}
        {type === "video" && (
          <div className="local_video_container">
            <video
              ref={localVideoRef}
              className="local_video"
              autoPlay
              playsInline
              muted
            />
          </div>
        )}

        {/* Call controls */}
        <div className="call_controls">
          {callStatus === "incoming" ? (
            <>
              <button
                className="control_btn reject"
                onClick={rejectCall}
                title="Rejeitar"
              >
                <EndCallIcon />
              </button>
              <button
                className="control_btn answer"
                onClick={answerCall}
                title="Atender"
              >
                {type === "video" ? (
                  <VideoIcon color="#fff" />
                ) : (
                  <PhoneIcon color="#fff" />
                )}
              </button>
            </>
          ) : (
            <>
              <button
                className={`control_btn ${isMuted ? "active" : ""}`}
                onClick={toggleMute}
                title={isMuted ? "Ativar microfone" : "Desativar microfone"}
              >
                {isMuted ? (
                  <MuteMicIcon color="#fff" />
                ) : (
                  <MicrophoneIcon color="#fff" />
                )}
              </button>

              {/* Botão de gravação */}
              {callStatus === "connected" && (
                <button
                  className={`control_btn record ${isRecording ? "recording" : ""}`}
                  onClick={isRecording ? stopRecording : startRecording}
                  title={isRecording ? "Parar gravação" : "Gravar chamada"}
                >
                  {isRecording ? (
                    <StopRecordIcon color="#fff" />
                  ) : (
                    <RecordIcon color="#fff" />
                  )}
                </button>
              )}

              {type === "video" && (
                <button
                  className={`control_btn ${isVideoOff ? "active" : ""}`}
                  onClick={toggleVideo}
                  title={isVideoOff ? "Ligar câmera" : "Desligar câmera"}
                >
                  {isVideoOff ? (
                    <CameraOffIcon color="#fff" />
                  ) : (
                    <VideoIcon color="#fff" />
                  )}
                </button>
              )}

              <button
                className="control_btn end_call"
                onClick={endCall}
                title="Encerrar chamada"
              >
                <EndCallIcon />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal de gravação salva */}
      {showRecordingModal && recordedBlob && (
        <div className="recording_modal_overlay">
          <div className="recording_modal">
            <h3>Gravação Salva</h3>
            <p>
              A chamada foi gravada com sucesso.
              <br />
              Duração: {formatDuration(recordingDuration)}
            </p>

            {type === "video" && (
              <div className="recording_preview">
                <video
                  src={URL.createObjectURL(recordedBlob)}
                  controls
                  style={{ maxWidth: "100%", maxHeight: "200px" }}
                />
              </div>
            )}

            {type === "voice" && (
              <div className="recording_preview">
                <audio
                  src={URL.createObjectURL(recordedBlob)}
                  controls
                  style={{ width: "100%" }}
                />
              </div>
            )}

            <div className="recording_modal_actions">
              <button
                className="recording_btn download"
                onClick={downloadRecording}
              >
                <DownloadIcon color="#fff" />
                Baixar Gravação
              </button>
              <button
                className="recording_btn discard"
                onClick={handleCloseRecordingModal}
              >
                Descartar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Incoming call notification component
export function IncomingCallNotification({ callData, onAnswer, onReject }) {
  const { type, caller } = callData;

  return (
    <div className="incoming_call_notification">
      <div className="incoming_call_info">
        <img
          src={
            caller?.picture ||
            "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png"
          }
          alt={caller?.name || "User"}
        />
        <div className="incoming_call_text">
          <h4>{caller?.name || "Desconhecido"}</h4>
          <p>Chamada de {type === "video" ? "vídeo" : "voz"} recebida...</p>
        </div>
      </div>
      <div className="incoming_call_actions">
        <button className="reject_btn" onClick={onReject}>
          <EndCallIcon />
        </button>
        <button className="answer_btn" onClick={onAnswer}>
          {type === "video" ? (
            <VideoIcon color="#fff" />
          ) : (
            <PhoneIcon color="#fff" />
          )}
        </button>
      </div>
    </div>
  );
}
